/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type InterpreterFrom } from 'xstate';
import { IllusEmpty_1, IllusLocation } from '@sg/garnish';

import { usePredictions } from '../../../../hooks';
import type {
  createLocationSearchMachine,
  LocationSearchMachineContext,
} from '../../../../machine';
import {
  LocationResults,
  LocationResultsListsContainer,
} from '../../../LocationResults';
import { LocationResultsListInfoMessage } from '../../../LocationResultsListInfoMessage';
import { LocationSearchPredictionsList } from '../../../LocationSearchPredictionsList';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchLocationsResults = memo(
  (props: LocationSearchLocationsResultsProps) => {
    const {
      locationSearchService,
      locationSearchType,
      locations,
      focusedLocationId,
      isLoading,
      isUsingSearchField,
      interactedLocationRestaurantSlug,
      withSafeAreaInsets,
      onLocationCardPress,
      onLocationCardFocus,
      onLocationResultsExpandCollapse,
    } = props;

    const { formatMessage } = useIntl();

    const {
      isFetchingPredictions,
      formattedPredictions,
      hasNoPredictions,
      predictionLocations,
      shouldRenderPredictions,
      selectPrediction,
    } = usePredictions({ locationSearchService });

    const { pickup: pickupLocations = [], outpost: outpostLocations = [] } =
      locations ?? {};

    const activeLocations =
      locationSearchType === 'outpost' ? outpostLocations : pickupLocations;
    const otherLocations =
      locationSearchType === 'outpost' ? pickupLocations : outpostLocations;

    const hasResolvedLocations = locations !== undefined;
    const noLocationsFound =
      hasResolvedLocations &&
      activeLocations.length + otherLocations.length === 0;

    const shouldRenderIntroMessage = !isLoading && !hasResolvedLocations;
    const shouldRenderNoLocationsFoundMessage = !isLoading && noLocationsFound;

    // ─────────────────────────────────────────────────────────────────────

    if (shouldRenderPredictions) {
      return (
        <LocationSearchPredictionsList
          predictions={formattedPredictions}
          predictionLocations={predictionLocations}
          locationSearchService={locationSearchService}
          onPress={selectPrediction}
          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
          onCardPress={onLocationCardPress}
          onCardFocus={onLocationCardFocus}
          isLoading={isFetchingPredictions}
          hasNoPredictions={hasNoPredictions}
        />
      );
    }

    if (shouldRenderIntroMessage) {
      return (
        <LocationResultsListInfoMessage
          illustration={IllusLocation}
          illustrationAriaLabel={formatMessage(
            messages.locationLocationIllustrationAlt,
          )}
          text={formatMessage(messages.locationEnableLocationServices)}
        />
      );
    }

    if (shouldRenderNoLocationsFoundMessage) {
      return (
        <LocationResultsListInfoMessage
          illustration={IllusEmpty_1}
          illustrationAriaLabel={formatMessage(
            messages.locationEmptyResultsIllustrationAlt,
          )}
          title={formatMessage(messages.locationNoResultsBanner)}
        />
      );
    }

    return (
      <LocationResultsListsContainer>
        <LocationResults
          locationSearchService={locationSearchService}
          locationSearchType={locationSearchType}
          locations={activeLocations}
          otherLocations={otherLocations}
          activePinLocationId={focusedLocationId}
          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
          isLoading={isLoading}
          isUsingSearchField={isUsingSearchField}
          withSafeAreaInsets={withSafeAreaInsets}
          shouldHideDetailsModal={false}
          onCardPress={onLocationCardPress}
          onCardFocus={onLocationCardFocus}
          onLocationResultsExpandCollapse={onLocationResultsExpandCollapse}
        />
      </LocationResultsListsContainer>
    );
  },
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationLocationIllustrationAlt: {
    defaultMessage: 'Sweetgreen customer with the location pin',
    description: 'Location | Location Illustration | Alt',
  },
  locationEnableLocationServices: {
    defaultMessage:
      'Enter an address above to get started. Enable location services to find nearby locations.',
    description: 'Location | Enable Location Services',
  },
  locationEmptyResultsIllustrationAlt: {
    defaultMessage: 'Seedless pea pod',
    description: 'Location | Empty Results Illustration | Alt',
  },
  locationNoResultsBanner: {
    defaultMessage: `Oops, we couldn't find any sweetgreen locations. Try another search`,
    description: 'Location | No Results | Banner',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchLocationsResultsProps = {
  locationSearchService: InterpreterFrom<ReturnType<typeof createLocationSearchMachine>>; // prettier-ignore
  locationSearchType: LocationSearchMachineContext['locationSearchType'];
  locations: LocationSearchMachineContext['locations'];
  isLoading: boolean;
  isUsingSearchField: boolean;
  interactedLocationRestaurantSlug: string | undefined;
  focusedLocationId?: string;
  withSafeAreaInsets?: boolean;
  onLocationCardPress: ComponentProps<typeof LocationResults>['onCardPress'];
  onLocationCardFocus: ComponentProps<typeof LocationResults>['onCardFocus'];
  onLocationResultsExpandCollapse: ComponentProps<
    typeof LocationResults
  >['onLocationResultsExpandCollapse'];
};
